// Phase A1 — Template AI Generator panel (multi-type).
//
// Generalised from the Phase N ``InvoiceTemplateGeneratorPanel``. One
// component now serves ``invoice`` / ``contract`` / ``ach`` flows; the
// caller picks via the ``templateType`` prop. Mounted from
// ``TemplateDetailPage`` for any builder template whose type matches.

import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
// Phase A2 — tabs + upload affordance.
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PdfPreview from "@/components/PdfPreview";
import { Sparkles, Loader2, RefreshCw, CheckCircle2, AlertTriangle, Upload, FileText, X as XIcon } from "lucide-react";

// Per-type config. Pure JSX/data — backend logic stays on the server.
// Note ``apiBase`` mirrors the Phase A1 router namespaces exactly:
//   * /admin/invoice-templates/* (Phase N — unchanged)
//   * /admin/contract-templates/* (Phase A1)
//   * /admin/ach-templates/* (Phase A1)
const CONFIG_BY_TYPE = {
    invoice: {
        apiBase: "/admin/invoice-templates",
        title: "AI invoice template generator",
        description: "Generate a fresh invoice body via Claude Sonnet — adjust category, tags, and tone, then preview and Apply.",
        categoryLabels: {
            standard: "Standard", detailed: "Detailed", minimalist: "Minimalist",
            compact: "Compact", branded: "Branded",
        },
        defaultAccent: "#3B82F6",
        loadingHint: "Claude is composing your invoice. ~5-15s.",
    },
    contract: {
        apiBase: "/admin/contract-templates",
        title: "AI rental-agreement template generator",
        description: "Generate a fresh rental agreement via Claude Sonnet — pick the form factor, choose tags, then preview and Apply.",
        categoryLabels: {
            standard: "Standard", short_form: "Short form",
            long_form: "Long form", branded: "Branded",
        },
        defaultAccent: "#1e3a8a",
        loadingHint: "Claude is drafting your rental agreement. ~10-25s.",
    },
    ach: {
        apiBase: "/admin/ach-templates",
        title: "AI ACH-authorization template generator",
        description: "Generate a one-page ACH debit authorization via Claude Sonnet — pick the form factor, choose tags, then preview and Apply.",
        categoryLabels: {
            standard: "Standard", recurring: "Recurring", branded: "Branded",
        },
        defaultAccent: "#0f766e",
        loadingHint: "Claude is drafting your ACH form. ~5-15s.",
    },
};

// Accept either the short form ("invoice"/"contract"/"ach") or the full
// backend enum ("invoice"/"rental_agreement"/"ach_authorization") so the
// caller can pass whatever it has on hand.
const _normalize = (t) => {
    if (t === "rental_agreement") return "contract";
    if (t === "ach_authorization") return "ach";
    return t || "invoice";
};

export default function TemplateAIGeneratorPanel({
    open, onOpenChange,
    templateType = "invoice",
    initialName = "",
    onApply, // (body_html) => void
    generatorEnabled = true,
}) {
    const ttype = _normalize(templateType);
    const cfg = CONFIG_BY_TYPE[ttype] || CONFIG_BY_TYPE.invoice;
    const categoryKeys = Object.keys(cfg.categoryLabels);

    const [catalog, setCatalog] = useState(null);
    const [name, setName] = useState(initialName);
    const [category, setCategory] = useState(categoryKeys[0]);
    const [tone, setTone] = useState("formal");
    const [accent, setAccent] = useState(cfg.defaultAccent);
    const [selectedTags, setSelectedTags] = useState(new Set());
    const [businessContext, setBusinessContext] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);

    // Phase A2 — upload-mode state. Kept separate from the prompt-mode
    // form state so switching tabs preserves both forms in-flight.
    const [mode, setMode] = useState("prompt"); // "prompt" | "upload"
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadInstructions, setUploadInstructions] = useState("");
    const [uploadBusy, setUploadBusy] = useState(false);
    // Step indicator for the upload flow. Fake-stepped client-side
    // (the backend doesn't push progress) — gives the user something
    // less anxiety-inducing than a 20s spinner. Steps:
    //   uploading → converting (PDF only) → extracting → tagging → done
    const [uploadStep, setUploadStep] = useState(""); // "" | step name
    const [uploadError, setUploadError] = useState("");
    const [uploadDragOver, setUploadDragOver] = useState(false);

    // Re-fetch the catalog whenever the dialog opens for a new type.
    useEffect(() => {
        if (!open) return;
        setCatalog(null);
        (async () => {
            try {
                const { data } = await api.get(`${cfg.apiBase}/catalog`);
                setCatalog(data);
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, cfg.apiBase]);

    // Reset transient state when re-opened (or type switches under us).
    useEffect(() => {
        if (open) {
            setName(initialName || "");
            setResult(null);
            setCategory(categoryKeys[0]);
            setAccent(cfg.defaultAccent);
            setSelectedTags(new Set());
            setBusinessContext("");
            if (previewBlobUrl) {
                URL.revokeObjectURL(previewBlobUrl);
                setPreviewBlobUrl(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, ttype]);

    const tagGroups = useMemo(() => (catalog ? catalog.tags : []), [catalog]);

    const toggleTag = (tag) => {
        setSelectedTags((prev) => {
            const next = new Set(prev);
            if (next.has(tag)) next.delete(tag);
            else next.add(tag);
            return next;
        });
    };

    const onGenerate = async () => {
        if (!name.trim()) {
            toast.error("Template name is required");
            return;
        }
        setBusy(true);
        setResult(null);
        if (previewBlobUrl) {
            URL.revokeObjectURL(previewBlobUrl);
            setPreviewBlobUrl(null);
        }
        try {
            const body = {
                name: name.trim(),
                category,
                tags: Array.from(selectedTags),
                tone,
                accent_color: category === "branded" ? accent : null,
                business_context: businessContext.trim().slice(0, 500) || null,
            };
            const { data } = await api.post(`${cfg.apiBase}/generate`, body);
            setResult(data);
            await renderPreview(data.body_html);
            toast.success(`Generated · ${data.used_tags.length} tags used`);
        } catch (err) {
            const code = err?.response?.status;
            if (code === 429) {
                const retry = err?.response?.headers?.["retry-after"] || "60";
                toast.error(`Slow down — wait ${retry}s before trying again.`);
            } else if (code === 503) {
                toast.error("Generator is disabled in system settings.");
            } else if (code === 502) {
                toast.error("Generator failed — try a different category or rephrase business context.");
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setBusy(false);
        }
    };

    const renderPreview = async (html) => {
        setPreviewBusy(true);
        try {
            const resp = await api.post(
                `${cfg.apiBase}/preview-pdf`,
                { body_html: html },
                { responseType: "blob" }
            );
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            setPreviewBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setPreviewBusy(false);
        }
    };

    const onApplyClick = () => {
        if (!result?.body_html) return;
        onApply?.(result.body_html);
        onOpenChange?.(false);
        toast.success("Template body applied to editor — remember to Save.");
    };

    // Phase A2 — upload-mode handlers.
    const _MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
    const _acceptedMimes = new Set([
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ]);
    const _acceptedExt = (n) => /\.(docx|pdf)$/i.test(n || "");

    const _validateFile = (f) => {
        if (!f) return "No file selected.";
        if (f.size > _MAX_UPLOAD_BYTES) {
            const mb = (f.size / 1024 / 1024).toFixed(1);
            return `File too large: ${mb} MB. Max is 5 MB.`;
        }
        if (!_acceptedMimes.has(f.type) && !_acceptedExt(f.name)) {
            return "Only .docx and .pdf are accepted.";
        }
        return "";
    };

    const onPickFile = (f) => {
        const err = _validateFile(f);
        setUploadError(err);
        setUploadFile(err ? null : f);
    };

    const onUpload = async () => {
        if (!uploadFile) {
            setUploadError("Pick a .docx or .pdf file first.");
            return;
        }
        setUploadBusy(true);
        setUploadError("");
        setResult(null);
        if (previewBlobUrl) {
            URL.revokeObjectURL(previewBlobUrl);
            setPreviewBlobUrl(null);
        }
        const isPdf = uploadFile.type === "application/pdf" || /\.pdf$/i.test(uploadFile.name);

        // Fake-stepped UI: kick off each label on a short timer so the
        // user sees progress even though the backend is one HTTP call.
        // The backend's CPU steps (pdf2docx + Mammoth + LLM) usually
        // take 8-25s combined; these timings are intentionally rough.
        let stepTimers = [];
        const setStep = (s, delay) => {
            stepTimers.push(setTimeout(() => setUploadStep(s), delay));
        };
        setUploadStep("uploading");
        setStep(isPdf ? "converting" : "extracting", 800);
        if (isPdf) setStep("extracting", 4000);
        setStep("tagging", isPdf ? 8000 : 4000);

        try {
            const fd = new FormData();
            fd.append("file", uploadFile);
            fd.append("template_type", _backendType(ttype));
            if (uploadInstructions.trim()) {
                fd.append("instructions", uploadInstructions.trim().slice(0, 500));
            }
            const { data } = await api.post(
                "/admin/templates/ai/from-upload",
                fd,
                { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 }
            );
            setUploadStep("done");
            setResult(data);
            await renderPreview(data.body_html);
            toast.success(`Generated · ${data.used_tags.length} tags used`);
        } catch (err) {
            const code = err?.response?.status;
            const detail = err?.response?.data?.detail || formatApiError(err);
            if (code === 413) setUploadError("File too large (server max 5 MB).");
            else if (code === 415) setUploadError(detail || "Only .docx and .pdf are accepted.");
            else if (code === 422) setUploadError(detail || "The file couldn't be converted. It may be image-only or password-protected.");
            else if (code === 429) {
                const retry = err?.response?.headers?.["retry-after"] || "60";
                setUploadError(`Upload rate limit — try again in ${retry}s.`);
            } else if (code === 503) setUploadError("Generator is disabled in system settings.");
            else setUploadError(detail || "Generation failed.");
            setUploadStep("error");
            toast.error(typeof detail === "string" ? detail : "Upload failed");
        } finally {
            stepTimers.forEach(clearTimeout);
            setUploadBusy(false);
        }
    };

    // Map the panel-side type alias to the backend enum.
    const _backendType = (t) => {
        if (t === "contract") return "rental_agreement";
        if (t === "ach") return "ach_authorization";
        return "invoice";
    };

    const _formatBytes = (n) => {
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
        return `${(n / 1024 / 1024).toFixed(1)} MB`;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-5xl max-h-[92vh] overflow-y-auto"
                data-testid={`template-ai-generator-dialog-${ttype}`}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <Sparkles size={18} className="text-blue-900" /> {cfg.title}
                    </DialogTitle>
                    <DialogDescription>{cfg.description}</DialogDescription>
                </DialogHeader>

                {!generatorEnabled && (
                    <div
                        className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2"
                        data-testid="generator-disabled-banner"
                    >
                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                        <span>Generator is disabled in system settings. Enable it to use this panel.</span>
                    </div>
                )}

                <Tabs value={mode} onValueChange={setMode} className="mt-2">
                    <TabsList className="grid grid-cols-2 max-w-sm" data-testid="gen-mode-tabs">
                        <TabsTrigger value="prompt" data-testid="gen-mode-prompt">
                            <Sparkles size={13} className="mr-1.5" /> From prompt
                        </TabsTrigger>
                        <TabsTrigger value="upload" data-testid="gen-mode-upload">
                            <Upload size={13} className="mr-1.5" /> From upload
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="prompt" className="mt-3">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-6 mt-2">
                    {/* LEFT: form */}
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="gen-name">Template name</Label>
                            <Input
                                id="gen-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Q3 branded template"
                                data-testid="gen-name-input"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Category</Label>
                                <Select value={category} onValueChange={setCategory}>
                                    <SelectTrigger data-testid="gen-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(cfg.categoryLabels).map(([v, l]) => (
                                            <SelectItem key={v} value={v} data-testid={`gen-category-${v}`}>{l}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Tone</Label>
                                <Select value={tone} onValueChange={setTone}>
                                    <SelectTrigger data-testid="gen-tone-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="formal" data-testid="gen-tone-formal">Formal</SelectItem>
                                        <SelectItem value="friendly" data-testid="gen-tone-friendly">Friendly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Accent color — only honored by the branded category. */}
                        <div className="space-y-1.5">
                            <Label htmlFor="gen-accent">
                                Accent color
                                {category !== "branded" && (
                                    <span className="text-xs text-slate-500 font-normal"> — only applied for the Branded category</span>
                                )}
                            </Label>
                            <div className="flex items-center gap-2">
                                <input
                                    id="gen-accent"
                                    type="color"
                                    value={accent}
                                    onChange={(e) => setAccent(e.target.value)}
                                    disabled={category !== "branded"}
                                    className="h-9 w-12 rounded border border-slate-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                    data-testid="gen-accent-color"
                                />
                                <Input
                                    value={accent}
                                    onChange={(e) => setAccent(e.target.value)}
                                    disabled={category !== "branded"}
                                    className="font-mono text-sm max-w-[140px]"
                                    placeholder="#3B82F6"
                                    data-testid="gen-accent-hex"
                                />
                                <div className="flex gap-1.5" data-testid="gen-accent-swatches">
                                    {["#1e3a8a", "#0f766e", "#b91c1c", "#d97706", "#7c3aed", "#0e7490"].map((c) => (
                                        <button
                                            type="button"
                                            key={c}
                                            disabled={category !== "branded"}
                                            onClick={() => setAccent(c)}
                                            className="h-6 w-6 rounded-full border border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-transform hover:scale-110"
                                            style={{ backgroundColor: c }}
                                            data-testid={`gen-accent-swatch-${c.replace("#", "")}`}
                                            aria-label={`Use ${c}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label>Tags <span className="text-xs text-slate-500 font-normal">— leave empty to let AI pick</span></Label>
                            <div
                                className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto p-2 border border-slate-200 rounded-md"
                                data-testid="gen-tag-picker"
                            >
                                {!catalog && <span className="text-xs text-slate-400">Loading catalog…</span>}
                                {(tagGroups || []).map((t) => {
                                    const on = selectedTags.has(t.tag);
                                    return (
                                        <button
                                            type="button"
                                            key={t.tag}
                                            onClick={() => toggleTag(t.tag)}
                                            className={`text-[11px] px-2 py-1 rounded-full font-mono border transition-colors ${
                                                on
                                                    ? "bg-blue-900 text-white border-blue-900"
                                                    : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                                            }`}
                                            data-testid={`gen-tag-chip-${t.tag}`}
                                        >
                                            {t.tag}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-slate-500">
                                {selectedTags.size === 0
                                    ? "AI will pick the best tags for this category."
                                    : `${selectedTags.size} tag${selectedTags.size === 1 ? "" : "s"} selected.`}
                            </p>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="gen-ctx">Business context <span className="text-xs text-slate-500 font-normal">(optional, max 500)</span></Label>
                            <Textarea
                                id="gen-ctx"
                                rows={3}
                                value={businessContext}
                                onChange={(e) => setBusinessContext(e.target.value.slice(0, 500))}
                                placeholder="e.g. Equipment rental company in Atlanta; net-30 terms; mostly trucking customers."
                                data-testid="gen-context-textarea"
                            />
                            <p className="text-[11px] text-slate-400 text-right">{businessContext.length} / 500</p>
                        </div>

                        <div className="flex gap-2 pt-1">
                            <Button
                                onClick={onGenerate}
                                disabled={busy || !generatorEnabled}
                                className="bg-blue-900 hover:bg-blue-800 gap-2"
                                data-testid="gen-generate-button"
                            >
                                {busy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate</>}
                            </Button>
                            {result && (
                                <Button
                                    variant="outline"
                                    onClick={onGenerate}
                                    disabled={busy}
                                    className="gap-2"
                                    data-testid="gen-regenerate-button"
                                >
                                    <RefreshCw size={14} /> Regenerate
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: preview */}
                    <div className="space-y-3">
                        <Label>PDF preview</Label>
                        {busy && (
                            <div
                                className="rounded-md border border-slate-200 bg-slate-50 px-6 py-16 text-center text-sm text-slate-600 flex flex-col items-center gap-3"
                                data-testid="gen-loading-state"
                            >
                                <Loader2 size={28} className="animate-spin text-blue-900" />
                                Generating template…
                                <span className="text-xs text-slate-500">{cfg.loadingHint}</span>
                            </div>
                        )}
                        {!busy && !result && (
                            <div
                                className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center text-sm text-slate-500"
                                data-testid="gen-empty-state"
                            >
                                Fill in the form and click <strong>Generate</strong> to preview.
                            </div>
                        )}
                        {!busy && result && (
                            <>
                                <PdfPreview
                                    src={previewBlobUrl}
                                    downloadHref={previewBlobUrl}
                                    downloadName="preview.pdf"
                                    testid="gen-pdf-preview"
                                />
                                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 space-y-1" data-testid="gen-result-meta">
                                    <div className="flex items-center gap-1.5">
                                        <CheckCircle2 size={12} className="text-emerald-700" />
                                        {result.used_tags.length} tag(s) used
                                        {result.tokens_used > 0 && <span className="text-slate-400">· {result.tokens_used} tokens</span>}
                                        {result.model && <span className="text-slate-400">· {result.model}</span>}
                                    </div>
                                    {result.unknown_tags?.length > 0 && (
                                        <div className="flex items-center gap-1.5 text-amber-700">
                                            <AlertTriangle size={12} />
                                            Unknown tags ignored: <span className="font-mono">{result.unknown_tags.join(", ")}</span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                    </TabsContent>

                    <TabsContent value="upload" className="mt-3">
                        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-6">
                            {/* LEFT — uploader + instructions */}
                            <div className="space-y-4">
                                <div className="space-y-1.5">
                                    <Label>Source file</Label>
                                    <label
                                        htmlFor="gen-upload-file"
                                        onDragOver={(e) => { e.preventDefault(); setUploadDragOver(true); }}
                                        onDragLeave={() => setUploadDragOver(false)}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setUploadDragOver(false);
                                            const f = e.dataTransfer.files?.[0];
                                            if (f) onPickFile(f);
                                        }}
                                        className={`block cursor-pointer rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
                                            uploadDragOver
                                                ? "border-blue-500 bg-blue-50"
                                                : "border-slate-300 bg-slate-50 hover:bg-slate-100"
                                        }`}
                                        data-testid="gen-upload-dropzone"
                                    >
                                        <Upload size={28} className="mx-auto text-slate-400" />
                                        <div className="mt-2 text-sm text-slate-700">
                                            <span className="font-medium text-blue-900">Click to browse</span> or drag-and-drop
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">.docx or .pdf · up to 5 MB</p>
                                        <input
                                            id="gen-upload-file"
                                            type="file"
                                            accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            onChange={(e) => onPickFile(e.target.files?.[0])}
                                            className="hidden"
                                            data-testid="gen-upload-input"
                                        />
                                    </label>
                                    {uploadFile && (
                                        <div
                                            className="mt-1 flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                                            data-testid="gen-upload-file-chip"
                                        >
                                            <FileText size={16} className="text-blue-900 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="truncate font-medium text-slate-800">{uploadFile.name}</div>
                                                <div className="text-xs text-slate-500">
                                                    {_formatBytes(uploadFile.size)} · {/\.pdf$/i.test(uploadFile.name) ? "PDF" : "DOCX"}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => { setUploadFile(null); setUploadError(""); }}
                                                className="text-slate-400 hover:text-slate-700"
                                                data-testid="gen-upload-clear"
                                                aria-label="Remove file"
                                            >
                                                <XIcon size={14} />
                                            </button>
                                        </div>
                                    )}
                                    {uploadError && (
                                        <p className="text-sm text-red-700" data-testid="gen-upload-error">{uploadError}</p>
                                    )}
                                </div>

                                <div className="space-y-1.5">
                                    <Label htmlFor="gen-upload-instructions">
                                        Additional instructions <span className="text-xs text-slate-500 font-normal">(optional, max 500)</span>
                                    </Label>
                                    <Textarea
                                        id="gen-upload-instructions"
                                        rows={3}
                                        value={uploadInstructions}
                                        onChange={(e) => setUploadInstructions(e.target.value.slice(0, 500))}
                                        placeholder="e.g. This is a month-to-month rental, use formal legal tone, the lessor address block is on page 2 footer."
                                        data-testid="gen-upload-instructions"
                                    />
                                    <p className="text-[11px] text-slate-400 text-right">{uploadInstructions.length} / 500</p>
                                </div>

                                <div className="flex gap-2 pt-1">
                                    <Button
                                        onClick={onUpload}
                                        disabled={uploadBusy || !uploadFile || !generatorEnabled}
                                        className="bg-blue-900 hover:bg-blue-800 gap-2"
                                        data-testid="gen-upload-generate-button"
                                    >
                                        {uploadBusy
                                            ? <><Loader2 size={14} className="animate-spin" /> Generating…</>
                                            : <><Sparkles size={14} /> Generate from upload</>}
                                    </Button>
                                    {result && !uploadBusy && (
                                        <Button
                                            variant="outline"
                                            onClick={onUpload}
                                            disabled={!uploadFile}
                                            className="gap-2"
                                            data-testid="gen-upload-regenerate-button"
                                        >
                                            <RefreshCw size={14} /> Regenerate
                                        </Button>
                                    )}
                                </div>

                                <p className="text-xs text-slate-500">
                                    This usually takes 10–20 seconds. PDFs go through an extra conversion step and may take longer.
                                </p>
                            </div>

                            {/* RIGHT — step indicator + preview */}
                            <div className="space-y-3">
                                <Label>PDF preview</Label>
                                {uploadBusy && (
                                    <div
                                        className="rounded-md border border-slate-200 bg-slate-50 px-6 py-10 text-sm text-slate-700"
                                        data-testid="gen-upload-step-indicator"
                                    >
                                        <ol className="space-y-2.5">
                                            {[
                                                { key: "uploading", label: "Uploading file" },
                                                { key: "converting", label: "Converting PDF to DOCX", pdfOnly: true },
                                                { key: "extracting", label: "Extracting layout" },
                                                { key: "tagging", label: "Inserting merge tags" },
                                            ].map((step) => {
                                                const isPdf = uploadFile && (uploadFile.type === "application/pdf" || /\.pdf$/i.test(uploadFile.name));
                                                if (step.pdfOnly && !isPdf) return null;
                                                const order = ["uploading", "converting", "extracting", "tagging", "done"];
                                                const cur = order.indexOf(uploadStep);
                                                const mine = order.indexOf(step.key);
                                                const done = cur > mine && cur !== -1;
                                                const active = uploadStep === step.key;
                                                return (
                                                    <li key={step.key} className="flex items-center gap-2.5">
                                                        {done ? (
                                                            <CheckCircle2 size={14} className="text-emerald-700 shrink-0" />
                                                        ) : active ? (
                                                            <Loader2 size={14} className="animate-spin text-blue-900 shrink-0" />
                                                        ) : (
                                                            <span className="inline-block h-3.5 w-3.5 rounded-full border border-slate-300 shrink-0" />
                                                        )}
                                                        <span className={done ? "text-slate-500" : active ? "text-slate-900 font-medium" : "text-slate-500"}>
                                                            {step.label}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ol>
                                    </div>
                                )}
                                {!uploadBusy && !result && (
                                    <div
                                        className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center text-sm text-slate-500"
                                        data-testid="gen-upload-empty-state"
                                    >
                                        Pick a file and click <strong>Generate from upload</strong>.
                                    </div>
                                )}
                                {!uploadBusy && result && (
                                    <>
                                        <PdfPreview
                                            src={previewBlobUrl}
                                            downloadHref={previewBlobUrl}
                                            downloadName="preview.pdf"
                                            testid="gen-upload-pdf-preview"
                                        />
                                        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 space-y-1" data-testid="gen-upload-result-meta">
                                            {result.source_hint && (
                                                <div className="flex items-center gap-1.5">
                                                    <FileText size={12} className="text-blue-900" />
                                                    <span className="text-slate-700">{result.source_hint}</span>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-1.5">
                                                <CheckCircle2 size={12} className="text-emerald-700" />
                                                {result.used_tags.length} tag(s) injected
                                                {result.tokens_used > 0 && <span className="text-slate-400">· {result.tokens_used} tokens</span>}
                                                {result.model && <span className="text-slate-400">· {result.model}</span>}
                                            </div>
                                            {result.unknown_tags?.length > 0 && (
                                                <div className="flex items-center gap-1.5 text-amber-700">
                                                    <AlertTriangle size={12} />
                                                    Unknown tags ignored: <span className="font-mono">{result.unknown_tags.join(", ")}</span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>

                <DialogFooter className="mt-4 gap-2">
                    <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="gen-close-button">
                        Close
                    </Button>
                    <Button
                        onClick={onApplyClick}
                        disabled={!result?.body_html || previewBusy}
                        className="bg-blue-900 hover:bg-blue-800"
                        data-testid="gen-apply-button"
                    >
                        Apply to editor
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
